// Google Calendar Integration Hook
// Handles OAuth flow, token management, and calendar CRUD operations
// Communicates with Supabase Edge Functions for secure token handling
// ROBUST: Supports shared admin token (info@gutachter-koop.de) for all Mitarbeiter

import { useState, useEffect, useCallback, useRef } from 'react'
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '@/lib/supabase'

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '967597285093-v79e3dq5hlfi6d0q1fp1fbuk01dbns98.apps.googleusercontent.com'
const REDIRECT_URI = `${window.location.origin}/auth/google/callback`
const SCOPES = 'https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/userinfo.email'

export interface GoogleCalendarEvent {
  id: string
  summary: string
  description: string
  location: string
  start: string
  end: string
  all_day: boolean
  status: string
  html_link: string
  color_id: string
  creator: string
  attendees: { email: string; name: string; status: string }[]
}

export interface GoogleCalendarInfo {
  id: string
  summary: string
  primary: boolean
  background_color: string
  foreground_color: string
}

interface CalendarState {
  isConnected: boolean
  isSharedConnection: boolean // true = using shared admin token, not own connection
  isLoading: boolean
  googleEmail: string | null
  error: string | null
  events: GoogleCalendarEvent[]
  calendars: GoogleCalendarInfo[]
}

// Generate a simple user ID based on the current session
function getUserId(): string {
  let uid = localStorage.getItem('gcal_user_id')
  if (!uid) {
    uid = 'user_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9)
    localStorage.setItem('gcal_user_id', uid)
  }
  return uid
}

async function callEdgeFunction(functionName: string, body: Record<string, unknown>) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/${functionName}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'apikey': SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(body),
  })

  const data = await res.json()
  
  if (!res.ok) {
    throw new Error(data.error || `Edge function error: ${res.status}`)
  }
  
  return data
}

export function useGoogleCalendar() {
  const [state, setState] = useState<CalendarState>({
    isConnected: false,
    isSharedConnection: false,
    isLoading: true,
    googleEmail: null,
    error: null,
    events: [],
    calendars: [],
  })

  const userId = useRef(getUserId())
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const statusChecked = useRef(false)

  // Check connection status on mount
  // ROBUST: Edge function falls back to shared info@gutachter-koop.de token
  // So even Mitarbeiter without own connection will get connected=true
  const checkStatus = useCallback(async () => {
    try {
      const data = await callEdgeFunction('google-calendar-auth', {
        action: 'status',
        user_id: userId.current,
      })

      if (data.connected) {
        // Detect if this is a shared connection (not the user's own)
        // If google_email is info@gutachter-koop.de but user didn't connect themselves
        const isShared = data.google_email === 'info@gutachter-koop.de' && 
          !localStorage.getItem('gcal_own_connection')

        // If token needs refresh, do it automatically
        if (data.needs_refresh) {
          try {
            await callEdgeFunction('google-calendar-auth', {
              action: 'refresh',
              user_id: userId.current,
            })
          } catch (refreshErr) {
            // Even if refresh fails, still mark as connected
            // The fetchEvents call will handle the actual token validity
            console.warn('Token refresh failed, will retry on fetch:', refreshErr)
          }
        }

        setState(prev => ({
          ...prev,
          isConnected: true,
          isSharedConnection: isShared,
          isLoading: false,
          googleEmail: data.google_email,
          error: null,
        }))
      } else {
        setState(prev => ({
          ...prev,
          isConnected: false,
          isSharedConnection: false,
          isLoading: false,
          googleEmail: null,
        }))
      }
    } catch (err) {
      // On error, don't block the calendar — just mark as not connected
      console.warn('Google Calendar status check failed:', err)
      setState(prev => ({
        ...prev,
        isConnected: false,
        isSharedConnection: false,
        isLoading: false,
        error: null, // Don't show error to user, just silently fall back to Supabase-only
      }))
    }
  }, [])

  useEffect(() => {
    if (!statusChecked.current) {
      statusChecked.current = true
      checkStatus()
    }
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
    }
  }, [checkStatus])

  // Start OAuth flow
  const connect = useCallback(() => {
    if (!GOOGLE_CLIENT_ID) {
      setState(prev => ({
        ...prev,
        error: 'Google Client ID nicht konfiguriert. Bitte VITE_GOOGLE_CLIENT_ID setzen.',
      }))
      return
    }

    // Mark that this user is connecting themselves
    localStorage.setItem('gcal_own_connection', 'true')
    // Store user_id for callback
    localStorage.setItem('gcal_oauth_state', userId.current)
    // State mit gcal_ Prefix fuer Routing im shared Callback
    const stateValue = `gcal_${userId.current}`

    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      response_type: 'code',
      scope: SCOPES,
      access_type: 'offline',
      prompt: 'consent',
      state: stateValue,
    })

    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`
  }, [])

  // Handle OAuth callback (call this from the callback page)
  // Returns true on success, or an error message string on failure
  const handleCallback = useCallback(async (code: string, state: string): Promise<true | string> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      // State-Prefix entfernen um die echte user_id zu bekommen
      const cleanUserId = state?.startsWith('gcal_') ? state.slice(5) : (state || userId.current)
      const data = await callEdgeFunction('google-calendar-auth', {
        action: 'exchange',
        code,
        redirect_uri: REDIRECT_URI,
        user_id: cleanUserId,
      })

      if (data.success) {
        localStorage.setItem('gcal_own_connection', 'true')
        setState(prev => ({
          ...prev,
          isConnected: true,
          isSharedConnection: false,
          isLoading: false,
          googleEmail: data.google_email,
          error: null,
        }))
        return true
      }

      // Edge function returned non-success
      const errMsg = data.error || 'Unbekannter Fehler beim Token-Austausch'
      setState(prev => ({ ...prev, isLoading: false, error: errMsg }))
      return errMsg
    } catch (err) {
      const errMsg = (err as Error).message
      setState(prev => ({ ...prev, isLoading: false, error: errMsg }))
      return errMsg
    }
  }, [])

  // Disconnect Google Calendar
  const disconnect = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true }))

    try {
      await callEdgeFunction('google-calendar-auth', {
        action: 'disconnect',
        user_id: userId.current,
      })

      localStorage.removeItem('gcal_own_connection')

      setState({
        isConnected: false,
        isSharedConnection: false,
        isLoading: false,
        googleEmail: null,
        error: null,
        events: [],
        calendars: [],
      })
    } catch (err) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: (err as Error).message,
      }))
    }
  }, [])

  // Fetch events from a single calendar
  const fetchEventsFromCalendar = useCallback(async (timeMin: string, timeMax: string, calendarId: string) => {
    try {
      const data = await callEdgeFunction('google-calendar-sync', {
        action: 'list',
        user_id: userId.current,
        time_min: timeMin,
        time_max: timeMax,
        calendar_id: calendarId,
      })
      return (data.events || []).map((e: GoogleCalendarEvent) => ({
        ...e,
        _calendar_id: calendarId,
      }))
    } catch {
      return []
    }
  }, [])

  // Fetch events for a date range — queries ALL calendars and merges results
  // ROBUST: Automatically retries with token refresh on failure
  const fetchEvents = useCallback(async (timeMin: string, timeMax: string, calendarId = 'primary') => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      // First, get all available calendars
      let calendarIds: string[] = [calendarId]
      try {
        const calData = await callEdgeFunction('google-calendar-sync', {
          action: 'calendars',
          user_id: userId.current,
        })
        if (calData.calendars && calData.calendars.length > 0) {
          // Include primary + all team/group calendars (exclude holidays & week numbers)
          calendarIds = calData.calendars
            .filter((c: GoogleCalendarInfo) => 
              !c.id.includes('#holiday@') && 
              !c.id.includes('#weeknum@')
            )
            .map((c: GoogleCalendarInfo) => c.id)
        }
      } catch {
        // If calendar list fails, fall back to just primary
        calendarIds = [calendarId]
      }

      // Fetch events from all calendars in parallel
      const allEventArrays = await Promise.all(
        calendarIds.map(cid => fetchEventsFromCalendar(timeMin, timeMax, cid))
      )

      // Merge and deduplicate by event ID
      const eventMap = new Map<string, GoogleCalendarEvent>()
      for (const events of allEventArrays) {
        for (const event of events) {
          if (!eventMap.has(event.id)) {
            eventMap.set(event.id, event)
          }
        }
      }
      const mergedEvents = Array.from(eventMap.values())
        .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())

      setState(prev => ({
        ...prev,
        isLoading: false,
        events: mergedEvents,
        error: null,
      }))

      return mergedEvents
    } catch (err) {
      const errorMsg = (err as Error).message
      
      // If token expired, try to refresh and retry once
      if (errorMsg.includes('reconnect') || errorMsg.includes('expired') || errorMsg.includes('401')) {
        try {
          await callEdgeFunction('google-calendar-auth', {
            action: 'refresh',
            user_id: userId.current,
          })
          // Retry with just primary after refresh
          const retryData = await callEdgeFunction('google-calendar-sync', {
            action: 'list',
            user_id: userId.current,
            time_min: timeMin,
            time_max: timeMax,
            calendar_id: calendarId,
          })
          setState(prev => ({
            ...prev,
            isLoading: false,
            events: retryData.events || [],
            error: null,
          }))
          return retryData.events || []
        } catch {
          // Refresh+retry failed — mark as disconnected
          setState(prev => ({
            ...prev,
            isConnected: false,
            isSharedConnection: false,
            isLoading: false,
            error: null, // Don't show error, just hide Google events
          }))
        }
      } else {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: null, // Don't show error to user
        }))
      }
      return []
    }
  }, [fetchEventsFromCalendar])

  // Create a new event
  const createEvent = useCallback(async (event: {
    summary: string
    description?: string
    location?: string
    start: string
    end: string
    all_day?: boolean
    attendees?: { email: string }[]
    color_id?: string
  }, calendarId = 'primary') => {
    try {
      const data = await callEdgeFunction('google-calendar-sync', {
        action: 'create',
        user_id: userId.current,
        calendar_id: calendarId,
        event,
      })

      if (data.success) {
        // Refresh events list
        return data.event
      }
    } catch (err) {
      setState(prev => ({
        ...prev,
        error: (err as Error).message,
      }))
    }
    return null
  }, [])

  // Update an existing event
  const updateEvent = useCallback(async (eventId: string, event: Partial<{
    summary: string
    description: string
    location: string
    start: string
    end: string
    all_day: boolean
  }>, calendarId = 'primary') => {
    try {
      const data = await callEdgeFunction('google-calendar-sync', {
        action: 'update',
        user_id: userId.current,
        calendar_id: calendarId,
        event_id: eventId,
        event,
      })

      return data.success || false
    } catch (err) {
      setState(prev => ({
        ...prev,
        error: (err as Error).message,
      }))
      return false
    }
  }, [])

  // Delete an event
  const deleteEvent = useCallback(async (eventId: string, calendarId = 'primary') => {
    try {
      const data = await callEdgeFunction('google-calendar-sync', {
        action: 'delete',
        user_id: userId.current,
        calendar_id: calendarId,
        event_id: eventId,
      })

      return data.success || false
    } catch (err) {
      setState(prev => ({
        ...prev,
        error: (err as Error).message,
      }))
      return false
    }
  }, [])

  // Fetch available calendars
  const fetchCalendars = useCallback(async () => {
    try {
      const data = await callEdgeFunction('google-calendar-sync', {
        action: 'calendars',
        user_id: userId.current,
      })

      setState(prev => ({
        ...prev,
        calendars: data.calendars || [],
      }))

      return data.calendars || []
    } catch (err) {
      setState(prev => ({
        ...prev,
        error: (err as Error).message,
      }))
      return []
    }
  }, [])

  return {
    ...state,
    connect,
    disconnect,
    handleCallback,
    fetchEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    fetchCalendars,
    checkStatus,
  }
}
